import React, { useState } from 'react'
import type { MuiTheme as Theme, StyleClasses, PresentationFilePreviewProps as FilePreviewProps } from '@paintscout/ui'
import { makeStyles, Grid, InputField, Tab, Tabs, PresentationFilePreview as FilePreview } from '@paintscout/ui'

import type { QuoteFile } from 'paintscout'

import debounce from 'lodash/debounce'

import type { WithUseStyles } from '@paintscout/ui/styles'
import UploadImage from '../PresentationUploadImage'
import { getYouTubeId } from '../EmbedMedia/getYouTubeId'

const useStyles = makeStyles(
  (_theme: Theme) => ({
    root: {},
    image: {},
    size: {},
    youtubePlaceholder: {
      aspectRatio: '16/9',
      height: 'unset'
    }
  }),
  { name: 'PresentationEmbedMedia' }
)

export interface PresentationEmbedMediaProps extends WithUseStyles<typeof useStyles> {
  value?: QuoteFile
  onChange: (file: QuoteFile) => void
  classes?: StyleClasses<typeof useStyles>

  className?: string
  disabled?: boolean
  accept?: string
  noClearButton?: boolean
  showSwapOnHover?: boolean
  square?: boolean
  maxWidth?: number
  maxHeight?: number
  buttonHeight?: number
  quality?: number
  youtube?: boolean
  format?: string
  showAllPages?: boolean
  filePreviewProps?: Partial<FilePreviewProps>
  companyId?: string
  s3UploadFeatureEnabled?: boolean
  s3MediaUploadEnabled?: boolean
}

/**
 * Presentation-editor variant of {@link EmbedMedia} (#21305). Same tabbed Upload/YouTube
 * glue, but composes the presentation UploadImage/FilePreview so the editor-only preview
 * features stay isolated from the core components.
 */
function PresentationEmbedMedia({
  value,
  onChange,
  accept,
  noClearButton,
  showSwapOnHover,
  square,
  maxWidth,
  maxHeight,
  buttonHeight,
  quality,
  disabled,
  youtube,
  format,
  showAllPages,
  filePreviewProps,
  companyId,
  s3UploadFeatureEnabled,
  s3MediaUploadEnabled,
  ...rest
}: PresentationEmbedMediaProps) {
  const mediaFormat = format ? format : value?.format
  const tab = mediaFormat === 'youtube' ? 'youtube' : 'upload'
  const classes = useStyles(rest)

  return (
    <Grid container spacing={1}>
      {youtube !== false && (
        <Grid item sm={12}>
          <Tabs value={tab === 'youtube' ? 1 : 0} onChange={handleTabChange}>
            <Tab label="Upload" />
            <Tab label="YouTube" />
          </Tabs>
        </Grid>
      )}
      <Grid item sm={12} className={classes.size}>
        {tab === 'upload' && (
          <UploadImage
            classes={classes}
            cloudinaryPublicId={value?.cloudinaryPublicId}
            s3PublicKey={value?.s3PublicKey}
            src={value?.src}
            format={value?.format}
            isUploading={value?.isUploading}
            presentationOptions={value?.presentationOptions}
            onUpload={handleUpload}
            onClear={handleClear}
            quality={quality}
            noClearButton={noClearButton}
            showSwapOnHover={showSwapOnHover}
            square={square}
            maxWidth={maxWidth}
            maxHeight={maxHeight}
            buttonHeight={buttonHeight}
            accept={accept}
            disabled={disabled}
            showAllPages={showAllPages}
            filePreviewProps={filePreviewProps}
            companyId={companyId}
            s3UploadFeatureEnabled={s3UploadFeatureEnabled}
            s3MediaUploadEnabled={s3MediaUploadEnabled}
          />
        )}
        {tab === 'youtube' && <YouTube onChange={onChange} value={value} disabled={disabled} />}
      </Grid>
    </Grid>
  )

  function handleTabChange(_event: any, tabIndex: number) {
    onChange({
      ...value,
      format: tabIndex === 1 ? 'youtube' : 'upload'
    })
  }

  async function handleUpload(args: {
    src: string
    cloudinaryPublicId: string
    format: string
    width: number
    height: number
    type: string
  }) {
    onChange({ ...args, visibility: 'visible' })
  }

  function handleClear() {
    onChange(null)
  }
}

export interface YouTubeProps {
  value?: QuoteFile
  onChange: (value: QuoteFile) => void
  disabled?: boolean
}

function YouTube({ onChange, value, disabled }: YouTubeProps) {
  const [unparsedUrl, setUnparsedUrl] = useState<string>(value?.youtubeId ?? '')

  const updateYoutubeVideo = debounce((unparsedUrl) => {
    const youtubeId = getYouTubeId(unparsedUrl)
    setUnparsedUrl(youtubeId)
    onChange({
      ...value,
      youtubeId
    })
  }, 1000)

  return (
    <Grid container spacing={3}>
      {!disabled && (
        <Grid item sm={12}>
          <InputField
            label={'YouTube URL or ID'}
            fullWidth={true}
            onChange={handleYouTubeUrlChange}
            value={unparsedUrl}
          />
        </Grid>
      )}
      <Grid item sm={12}>
        {value?.youtubeId && <FilePreview file={value} />}
      </Grid>
    </Grid>
  )

  function handleYouTubeUrlChange(ev: React.ChangeEvent<HTMLInputElement>) {
    // https://www.youtube.com/watch?v=Y-UqcW4bQLE
    setUnparsedUrl(ev.target.value)
    updateYoutubeVideo(ev.target.value)
  }
}

export default PresentationEmbedMedia
